import isNull from 'lodash/isNull';
import { FormattedMessage } from 'react-intl';

import { get } from 'utils/lodash';
import { formatArrayKeys, removeStringComma } from 'utils/formatter';

import { CURRENCY_IDR, DEFAULT_INT_ZERO, PRODUCT_CODE_NOTE_ADMIN_FEE } from 'settings/constants/common';
import { DEFAULT_MAX_TRANSFER_AMOUNT, DEFAULT_MIN_TRANSFER_AMOUNT } from 'settings/constants/transaction';
import { getInterbankFavouriteDetail, getInterbankTransferDetail } from 'middleware/actions/transfer';

export const getInterbankConfirmationBody = data => {
  const favTransaction = get(data, 'favouriteTransaction', false);
  const favIndex = get(data, 'favouriteIndex', null);

  const bankCode = get(data, 'bankCode', '');
  const allBank = get(data, 'allBank', '');
  const receiverAccountNumber = get(data, 'accountNumber', '');

  return {
    userId: get(data, 'userId', ''),
    senderAccountNumber: get(data, 'senderAccount.accountNumber', ''), // fromAccountNumber
    senderCurrency: get(data, 'senderAccount.currencyCodeIso', CURRENCY_IDR), // fromCurrency
    receiverEmail: get(data, 'beneficiaryEmail', ''), // toAccountEmailAddress
    receiveAmount: removeStringComma(get(data, 'transferAmount', '')), // toAmount
    receiveMessage: get(data, 'message', ''), // beneReferenceNo
    ...(favTransaction ? { favIndex, favTransaction } : { bankCode, allBank, receiverAccountNumber }),
  };
};

export const getInterbankReceiverInfos = ({ confirmationDetail, referenceNumber, isResultSuccess }) => {
  const message = get(confirmationDetail, 'beneReferenceNo', '-');
  const bank = get(confirmationDetail, 'bankName', '');
  const effectiveDate = get(confirmationDetail, 'effectiveDateDisplay', '');
  const email = get(confirmationDetail, 'toAccountEmailAddress', '-');

  const defaultInfos = [
    { title: <FormattedMessage id="transaction.transfer.interbank.beneficiaryBank" />, info: bank },
    { title: <FormattedMessage id="transaction.transfer.effectiveDate" />, info: effectiveDate },
    { title: <FormattedMessage id="transaction.transfer.message" />, info: message },
    { title: <FormattedMessage id="transaction.transfer.interbank.beneficiaryEmail" />, info: email },
  ];

  const successfulInfos = defaultInfos.concat({
    title: <FormattedMessage id="transaction.transfer.referenceNumber" />,
    info: referenceNumber,
  });

  return isResultSuccess ? successfulInfos : defaultInfos;
};

export const getInterbankMonetaries = (detail, senderAccount) => {
  const debitedAmount = get(detail, 'toAmountDis', '');
  const transactionFee = get(detail, 'chargesDis', '');
  const transactionAmount = get(detail, 'fromAmountDis', '');
  const localDebitedAmount = get(detail, 'localAmountDis', '');
  const productCode = get(senderAccount, 'productCode', '');

  const showNoteAdminFee = PRODUCT_CODE_NOTE_ADMIN_FEE.includes(productCode);

  const resultAllMonetaryInformation = [
    { title: <FormattedMessage id="transaction.transfer.transactionAmount" />, info: transactionAmount },
    { title: <FormattedMessage id="transaction.transfer.transactionFee" />, info: transactionFee },
    { title: <FormattedMessage id="transaction.transfer.totalDebit" />, info: debitedAmount },
    { title: <FormattedMessage id="transaction.transfer.localDebitedAmount" />, info: localDebitedAmount },
  ];

  const resultMonetaryInformationWithAdminNote = [
    { title: <FormattedMessage id="transaction.transfer.transactionAmount" />, info: transactionAmount },
    { title: <FormattedMessage id="transaction.transfer.transactionFee" />, info: transactionFee },
    {
      title: (
        <span>
          <span>*</span>
          <FormattedMessage id="transaction.transfer.changeAdminFee" />
        </span>
      ),
    },
  ];

  return !showNoteAdminFee ? resultAllMonetaryInformation : resultMonetaryInformationWithAdminNote;
};

export const getInterbankDynamicFields = (detail, isFavouriteTransfer) => {
  const currency = get(detail, 'senderAccount.currency', CURRENCY_IDR);
  const minAmount = get(detail, 'serviceInfoBean.minLimit', DEFAULT_INT_ZERO);
  const maxAmount = get(detail, 'serviceInfoBean.maxLimit', DEFAULT_INT_ZERO);
  const currentBalance = get(detail, 'senderAccount.plainBalance', DEFAULT_INT_ZERO);

  const minAmountValue = minAmount > DEFAULT_INT_ZERO ? minAmount : DEFAULT_MIN_TRANSFER_AMOUNT;

  return [
    {
      name: 'accountNumber',
      readOnly: isFavouriteTransfer,
    },
    {
      name: 'transferAmount',
      validationRules: [
        { type: 'required', isValidateRequired: true },
        {
          type: 'senderBalance',
          value: currentBalance,
          isValidateRequired: currency === CURRENCY_IDR,
        },
        {
          type: 'minAmount',
          value: minAmountValue,
          isValidateRequired: true,
        },
        {
          type: 'maxAmount',
          value: maxAmount > minAmountValue ? maxAmount : DEFAULT_MAX_TRANSFER_AMOUNT,
          isValidateRequired: true,
        },
      ],
    },
  ];
};

export const getInterbankFavouriteDynamicFields = detail => {
  const banks = get(detail, 'bankList', []);

  return [
    {
      options: formatArrayKeys(banks),
      name: 'bankCode',
    },
  ];
};

export const getInterbankFavouriteTransferDetail = (detail, isEditFavourite, formDetail) => {
  const bankName = get(detail, 'bankName', '');
  const bankCode = get(detail, 'bankCode', '');
  const toAccountNo = get(detail, 'toAccountNo', '');
  const emailAddress = get(detail, 'emailAddress', '') || get(formDetail, 'beneficiaryEmail', '');
  const accountNickname = get(detail, 'accountNickname', '');
  const toAccountHolderName = get(detail, 'toAccountHolderName', '');

  return { bankName, bankCode, toAccountNo, toAccountHolderName, emailAddress, ...(isEditFavourite && { accountNickname }) };
};

export const getInterbankFavouriteBeneficiaryDetails = detail => [
  {
    title: <FormattedMessage id="transaction.favourite.form.beneficiaryNickname" />,
    info: get(detail, 'accountNickname', '-'),
  },
  {
    title: <FormattedMessage id="transaction.transfer.beneficiary.bank" />,
    info: get(detail, 'bankName', '-'),
  },
  {
    title: <FormattedMessage id="transaction.transfer.beneficiary.email" />,
    info: get(detail, 'emailAddress', '-'),
  },
];

export const getInterbankEditFavouriteResultPayload = detail => ({
  userId: get(detail, 'userId', ''),
  tacValue: get(detail, 'tacValue', ''),
  restSMSService: get(detail, 'tacDetail.tacViewBean.restSMSService', {}),
  accountNickname: get(detail, 'confirmationDetail.accountNickname', ''),
  toAccountNo: get(detail, 'confirmationDetail.toAccountNo', ''),
  bankCode: get(detail, 'confirmationDetail.bankCode', ''),
  emailAddress: get(detail, 'confirmationDetail.emailAddress', ''),
});

export const getInterbankDeleteFavouriteResultPayload = detail => ({
  userId: get(detail, 'userId', ''),
  tacValue: get(detail, 'tacValue', ''),
  restSMSService: get(detail, 'tacDetail.tacViewBean.restSMSService', {}),
  toAccountNo: get(detail, 'confirmationDetail.toAccountNo', ''),
  bankCode: get(detail, 'confirmationDetail.bankCode', ''),
});

export const getInterbankDetailDispatcher = ({ userId, favouriteIndex, favouriteAccount, isSenderIDR, dispatch }) => {
  const favAccountMaps = get(favouriteAccount, 'favouriteAccountMap', {});

  if (!isSenderIDR) return getInterbankTransferDetail();

  return !isNull(favouriteIndex)
    ? dispatch(getInterbankFavouriteDetail({ userId, favIndex: favouriteIndex, favAccountMaps }))
    : dispatch(getInterbankTransferDetail({ userId }));
};
